/**
 * ExternalFormConnector — Google Forms tab inside the registration Form Builder.
 *
 * Lets an event admin:
 *   1. Paste a public Google Forms URL
 *   2. Preview the form (parses Google's public payload server-side)
 *   3. Either embed it on the public registration page, or
 *      Import every question as a native EventFlow custom field (so answers
 *      flow through delegate exports, badge designer, fee gates, etc.)
 *
 * No Google Cloud credentials needed — only public form data is read.
 */
import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { externalFormAPI, getErrorMessage } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Loader2, ExternalLink, Eye, Download, AlertCircle, CheckCircle2, FileText, Link as LinkIcon, RefreshCw, Sheet,
} from 'lucide-react';
import { authHeaders } from '@/lib/authToken';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FORM_URL_PLACEHOLDER = 'https://docs.google.com/forms/d/e/.../viewform';
const HELP_LINK = 'https://support.google.com/docs/answer/2839737';

export const ExternalFormConnector = ({ eventId }) => {
  const qc = useQueryClient();
  const [formUrl, setFormUrl] = useState('');
  const [displayMode, setDisplayMode] = useState('embed');
  const [enabled, setEnabled] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [showNativeForm, setShowNativeForm] = useState(false);

  const { data: cfg, isLoading } = useQuery({
    queryKey: ['event-external-form', eventId],
    queryFn: () => externalFormAPI.getConfig(eventId).then((r) => r.data),
    enabled: !!eventId,
    onSuccess: (d) => {
      if (d) {
        setFormUrl(d.form_url || '');
        setDisplayMode(d.display_mode || 'embed');
        setEnabled(!!d.enabled);
        setShowNativeForm(!!d.show_native_form);
      }
    },
  });

  // onSuccess in useQuery v5 lives inside the queryClient; mirror defaults once when data arrives
  React.useEffect(() => {
    if (cfg) {
      setFormUrl(cfg.form_url || '');
      setDisplayMode(cfg.display_mode || 'embed');
      setEnabled(!!cfg.enabled);
      setShowNativeForm(!!cfg.show_native_form);
    }
  }, [cfg]);

  const saveMutation = useMutation({
    mutationFn: () => externalFormAPI.saveConfig(eventId, {
      enabled, form_url: formUrl, display_mode: displayMode, show_native_form: showNativeForm,
    }),
    onSuccess: () => {
      toast.success('External form settings saved');
      qc.invalidateQueries({ queryKey: ['event-external-form', eventId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Could not save settings')),
  });

  const previewMutation = useMutation({
    mutationFn: () => externalFormAPI.preview(eventId, { form_url: formUrl }),
    onSuccess: (r) => {
      setPreviewData(r.data);
      toast.success(`Found ${r.data.field_count} question${r.data.field_count === 1 ? '' : 's'}`);
    },
    onError: (e) => {
      setPreviewData(null);
      toast.error(getErrorMessage(e, 'Could not read the form'));
    },
  });

  const importMutation = useMutation({
    mutationFn: () => externalFormAPI.importFields(eventId, { form_url: formUrl }),
    onSuccess: (r) => {
      toast.success(`Imported ${r.data.imported} field(s) from "${r.data.form_title || 'Google Form'}"`);
      qc.invalidateQueries({ queryKey: ['custom-fields', eventId] });
      qc.invalidateQueries({ queryKey: ['admin-custom-fields', eventId] });
    },
    onError: (e) => toast.error(getErrorMessage(e, 'Import failed')),
  });

  const embedUrl = cfg?.embed_url || (formUrl && formUrl.includes('docs.google.com') ? `${formUrl.split('?')[0]}?embedded=true` : '');

  return (
    <div className="space-y-5" data-testid="external-form-connector">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-emerald-600" />
            Connect a Google Form
          </CardTitle>
          <CardDescription>
            Use an existing Google Form for registration, or import its questions into the native EventFlow form so answers land in delegate exports, badge designer and the fee gate. Your form must be public ("Anyone with the link can respond").{' '}
            <a href={HELP_LINK} target="_blank" rel="noopener noreferrer" className="text-emerald-700 underline">How to share?</a>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <>
              <div>
                <Label htmlFor="ef-url">Google Form URL</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    id="ef-url"
                    data-testid="external-form-url-input"
                    placeholder={FORM_URL_PLACEHOLDER}
                    value={formUrl}
                    onChange={(e) => setFormUrl(e.target.value)}
                    className="font-mono text-sm"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    data-testid="external-form-preview-btn"
                    disabled={!formUrl || previewMutation.isPending}
                    onClick={() => previewMutation.mutate()}
                  >
                    {previewMutation.isPending ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Reading…</>
                    ) : (
                      <><Eye className="h-4 w-4 mr-2" />Preview</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-1">Paste the form's <code>viewform</code> link — short <code>forms.gle/…</code> links also work.</p>
              </div>

              {previewData && (
                <Alert className="border-emerald-200 bg-emerald-50">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <AlertDescription className="text-emerald-900">
                    <div className="font-medium mb-1">{previewData.title || 'Untitled form'}</div>
                    <div className="text-sm">Detected <strong>{previewData.field_count}</strong> question(s).</div>
                    {previewData.fields && previewData.fields.length > 0 && (
                      <ul className="mt-2 max-h-40 overflow-y-auto text-xs space-y-1" data-testid="external-form-preview-list">
                        {previewData.fields.slice(0, 20).map((f, i) => (
                          <li key={i} className="flex items-center gap-2">
                            <Badge variant="secondary" className="text-[10px] uppercase">{f.field_type}</Badge>
                            <span className="truncate">{f.label}</span>
                            {f.required && <span className="text-rose-600">*</span>}
                          </li>
                        ))}
                        {previewData.fields.length > 20 && (
                          <li className="text-slate-500">… and {previewData.fields.length - 20} more</li>
                        )}
                      </ul>
                    )}
                  </AlertDescription>
                </Alert>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-3 rounded-lg border bg-white">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="ef-enabled" className="text-sm font-medium">Use this form for registration</Label>
                      <p className="text-xs text-slate-500 mt-0.5">When on, delegates see the Google Form on the public registration page instead of (or alongside) the native form.</p>
                    </div>
                    <Switch
                      id="ef-enabled"
                      data-testid="external-form-enabled-toggle"
                      checked={enabled}
                      onCheckedChange={setEnabled}
                    />
                  </div>
                </div>

                <div className="p-3 rounded-lg border bg-white">
                  <Label htmlFor="ef-mode" className="text-sm font-medium">Display mode</Label>
                  <Select value={displayMode} onValueChange={setDisplayMode}>
                    <SelectTrigger id="ef-mode" className="mt-1" data-testid="external-form-mode-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="embed">Embed (iframe on registration page)</SelectItem>
                      <SelectItem value="redirect">Redirect (open Google Forms in a new tab)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-3 rounded-lg border bg-white md:col-span-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="ef-also-native" className="text-sm font-medium">Also show the native EventFlow form</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Useful if you want Google Forms for the rich Q&amp;A but still need our payment / approval flow on the same page.</p>
                    </div>
                    <Switch
                      id="ef-also-native"
                      data-testid="external-form-show-native-toggle"
                      checked={showNativeForm}
                      onCheckedChange={setShowNativeForm}
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-2">
                <Button
                  onClick={() => saveMutation.mutate()}
                  disabled={saveMutation.isPending}
                  data-testid="external-form-save-btn"
                >
                  {saveMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving…</>
                  ) : (
                    'Save settings'
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => importMutation.mutate()}
                  disabled={!formUrl || importMutation.isPending}
                  data-testid="external-form-import-btn"
                >
                  {importMutation.isPending ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Importing…</>
                  ) : (
                    <><Download className="h-4 w-4 mr-2" />Import questions as native fields</>
                  )}
                </Button>
                {formUrl && (
                  <a
                    href={formUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-emerald-700 hover:underline"
                  >
                    <ExternalLink className="h-4 w-4" />Open on Google Forms
                  </a>
                )}
              </div>

              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  Google Forms responses are stored on Google's side — only an "Import questions" copies the structure into EventFlow. To pull live responses into delegate records, link your form to a Google Sheet and use our standard CSV import on /admin/delegates.
                </AlertDescription>
              </Alert>
            </>
          )}
        </CardContent>
      </Card>

      {/* Live preview of the embed exactly as delegates will see it */}
      {enabled && embedUrl && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <LinkIcon className="h-4 w-4" />
              Live preview
            </CardTitle>
            <CardDescription>This is exactly what delegates see on your registration page.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg overflow-hidden border">
              <iframe
                src={embedUrl}
                title="Google Form preview"
                width="100%"
                height="600"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
                data-testid="external-form-preview-iframe"
              >
                Loading…
              </iframe>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Iter 303: one-click "import responses" from the linked Google Sheet */}
      <SheetSyncCard eventId={eventId} />
    </div>
  );
};

function SheetSyncCard({ eventId }) {
  const [sheetId, setSheetId] = useState('');
  const [gid, setGid] = useState('0');
  const [headers, setHeaders] = useState(null);
  const [mapping, setMapping] = useState({
    email: 'Email Address', first_name: 'First Name',
    last_name: 'Last Name', phone: 'Phone',
  });
  const [result, setResult] = useState(null);

  const api = async (path, opts = {}) => {
    const r = await fetch(`${API_URL}${path}`, {
      ...opts,
      headers: { 'Content-Type': 'application/json', ...authHeaders(), ...(opts.headers || {}) },
    });
    if (!r.ok) {
      const e = await r.json().catch(() => ({}));
      throw new Error(e.detail || `Request failed (${r.status})`);
    }
    return r.json();
  };

  const previewHeaders = useMutation({
    mutationFn: () => api(`/api/admin/events/${eventId}/external-form/sheet-headers?sheet_id=${encodeURIComponent(sheetId)}&gid=${encodeURIComponent(gid)}`),
    onSuccess: (r) => {
      setHeaders(r.headers || []);
      // Auto-guess mapping: look for case-insensitive substring match
      const guess = { ...mapping };
      const findCol = (needles) => r.headers.find(h => needles.some(n => h.toLowerCase().includes(n))) || '';
      guess.email = findCol(['email', 'e-mail', 'mail']) || mapping.email;
      guess.first_name = findCol(['first name', 'firstname', 'given name']) || mapping.first_name;
      guess.last_name = findCol(['last name', 'lastname', 'surname', 'family name']) || mapping.last_name;
      guess.phone = findCol(['phone', 'mobile', 'cell', 'whatsapp', 'tel']) || mapping.phone;
      setMapping(guess);
      toast.success(`Loaded ${r.headers.length} column${r.headers.length === 1 ? '' : 's'} — auto-mapped what we could`);
    },
    onError: (e) => toast.error(e.message),
  });

  const sync = useMutation({
    mutationFn: () => api(`/api/admin/events/${eventId}/external-form/sync-sheet`, {
      method: 'POST', body: JSON.stringify({ sheet_id: sheetId, gid, mapping }),
    }),
    onSuccess: (r) => {
      setResult(r);
      toast.success(`Sync complete — ${r.new} new, ${r.updated} updated, ${r.skipped} skipped`);
    },
    onError: (e) => toast.error(e.message),
  });

  const parseSheetId = (raw) => {
    const m = (raw || '').match(/\/spreadsheets\/d\/([a-zA-Z0-9_-]+)/);
    return m ? m[1] : raw;
  };

  return (
    <Card data-testid="sheet-sync-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sheet className="h-4 w-4 text-emerald-700" />
          Sync responses from Google Sheets
        </CardTitle>
        <CardDescription>
          Paste the Google Sheet URL that your Form is sending responses to. The Sheet must be share-set to "Anyone with the link can view". Re-running is safe — existing delegates get updated, new ones get registered.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <Label>Sheet URL or ID *</Label>
            <Input
              value={sheetId}
              onChange={e => setSheetId(parseSheetId(e.target.value))}
              placeholder="https://docs.google.com/spreadsheets/d/…/edit"
              data-testid="sheet-sync-id"
            />
          </div>
          <div>
            <Label>Tab gid</Label>
            <Input value={gid} onChange={e => setGid(e.target.value)} placeholder="0" data-testid="sheet-sync-gid" />
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => previewHeaders.mutate()} disabled={!sheetId || previewHeaders.isPending} data-testid="sheet-sync-preview-btn">
            {previewHeaders.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
            Preview columns
          </Button>
          <Button onClick={() => sync.mutate()} disabled={!sheetId || sync.isPending} data-testid="sheet-sync-btn">
            {sync.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
            Sync responses → Delegates
          </Button>
        </div>

        {headers && headers.length > 0 && (
          <div className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid="sheet-sync-mapping">
            <p className="text-xs font-medium text-slate-700">Column mapping <span className="text-[10px] text-slate-400">— pick which Sheet column feeds each delegate field</span></p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {[
                { key: 'email', label: 'Email *' },
                { key: 'first_name', label: 'First name' },
                { key: 'last_name', label: 'Last name' },
                { key: 'phone', label: 'Phone' },
              ].map(({ key, label }) => (
                <div key={key} className="flex items-center gap-2">
                  <Label className="w-24 text-xs">{label}</Label>
                  <Select value={mapping[key] || 'none'} onValueChange={(v) => setMapping({ ...mapping, [key]: v === 'none' ? '' : v })}>
                    <SelectTrigger className="flex-1" data-testid={`sheet-sync-map-${key}`}><SelectValue placeholder="Not mapped" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Not in sheet —</SelectItem>
                      {headers.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
        )}

        {result && (
          <div className="text-xs border rounded p-2 bg-emerald-50 border-emerald-200 text-emerald-900" data-testid="sheet-sync-result">
            <p><strong>{result.new}</strong> new · <strong>{result.updated}</strong> updated · <strong>{result.skipped}</strong> skipped · {result.total_rows} total rows</p>
            {result.errors?.length > 0 && (
              <ul className="mt-1 text-rose-700 list-disc pl-4">
                {result.errors.map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ExternalFormConnector;
